import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

/**
 * Pantalla de error de marca RedMobility.
 *
 * Reemplaza las pantallas en blanco / alerts del navegador cuando un proceso
 * de la plataforma falla (pago, validación, carga de datos, errores de React).
 * Siempre ofrece una salida: volver al paso anterior y/o reintentar.
 *
 * Props:
 *  - title:      título (por defecto, mensaje genérico de error).
 *  - message:    descripción del error.
 *  - onRetry:    si se pasa, muestra botón "Reintentar".
 *  - onBack:     si se pasa, muestra botón "Volver" (al paso anterior).
 *  - retryLabel / backLabel: textos personalizados de los botones.
 *  - inline:     si true, ocupa solo su contenedor (no toda la pantalla).
 *  - icon:       ícono lucide (por defecto AlertTriangle).
 *  - className:  clases extra.
 */
export default function ErrorScreen({
  title,
  message,
  onRetry,
  onBack,
  retryLabel,
  backLabel,
  inline = false,
  icon: Icon = AlertTriangle,
  className = '',
}) {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`${
        inline
          ? 'flex flex-col items-center justify-center py-16'
          : 'min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4'
      } ${className}`}
    >
      <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-5">
        <Icon className="w-10 h-10 text-red-500" />
      </div>
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-[#111827] mb-3 text-center">
        {title || t('errorScreen.title', 'Algo salió mal')}
      </h2>
      <p className="text-gray-500 max-w-md text-center mb-8 leading-relaxed">
        {message || t('errorScreen.message', 'Ocurrió un error al procesar tu solicitud. Vuelve al paso anterior e inténtalo de nuevo.')}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white font-bold shadow-md hover:scale-105 transition-transform"
          >
            <RefreshCw className="w-4 h-4" /> {retryLabel || t('common.retry', 'Reintentar')}
          </button>
        )}
        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {backLabel || t('common.back', 'Volver')}
          </button>
        )}
      </div>
    </div>
  );
}
